import { useEffect, useRef } from 'react';

/** How a pending row re-checks itself; set by the agent per quote lane. */
export type AutoRefreshMode = 'once' | 'poll';

/** Px/ZSP settle in seconds: a quick check, then one retry. */
const ONCE_DELAYS_MS = [3_000, 10_000] as const;
/** P100/P250 backoff; the last step repeats until the cap. */
const POLL_DELAYS_MS = [5_000, 10_000, 20_000, 30_000] as const;
/** Stop polling after this long; "Check status" stays as the fallback. */
export const POLL_CAP_MS = 5 * 60_000;

/** Delay before check `n` (0-based), or undefined once the schedule is done. */
export function nextDelay(
  mode: AutoRefreshMode,
  n: number,
  elapsedMs: number
): number | undefined {
  if (mode === 'once') return ONCE_DELAYS_MS[n];
  const delay = POLL_DELAYS_MS[Math.min(n, POLL_DELAYS_MS.length - 1)];
  return elapsedMs + delay <= POLL_CAP_MS ? delay : undefined;
}

/**
 * Fires `onRefresh` on a schedule while the row is pending, so a quote
 * resolved server-side shows up without a click. A check due while the tab
 * is hidden runs when it becomes visible; stops when the row resolves or unmounts.
 */
export function useAutoRefresh(
  mode: AutoRefreshMode | undefined,
  pending: boolean,
  onRefresh: (() => void) | undefined
): void {
  // Latest callback without restarting the schedule on every render.
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  const canRefresh = Boolean(onRefresh);

  useEffect(() => {
    if (!mode || !pending || !canRefresh) return;
    let n = 0;
    let elapsed = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let onVisible: (() => void) | undefined;

    // A due check waits for the tab to be visible, so a hidden tab never
    // spends the schedule without actually checking.
    const tick = (delay: number) => {
      if (document.visibilityState === 'hidden') {
        const listener = () => {
          if (document.visibilityState === 'hidden') return;
          document.removeEventListener('visibilitychange', listener);
          onVisible = undefined;
          tick(delay);
        };
        onVisible = listener;
        document.addEventListener('visibilitychange', listener);
        return;
      }
      elapsed += delay;
      n += 1;
      refreshRef.current?.();
      schedule();
    };

    const schedule = () => {
      const delay = nextDelay(mode, n, elapsed);
      if (delay === undefined) return;
      timer = setTimeout(() => tick(delay), delay);
    };

    schedule();
    return () => {
      clearTimeout(timer);
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
    };
  }, [mode, pending, canRefresh]);
}
